"use client";
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Tooltip, notification } from "antd";
import {
  EditOutlined,
  ExclamationCircleOutlined,
  LeftOutlined,
  RightOutlined,
  CalendarOutlined,
  SyncOutlined,
  FieldTimeOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { fetchMedications, takeDose } from "../../redux/thunks/medicationThunks";
import {
  selectMedicationState,
  selectActiveMedications,
  selectMedicationReminder,
  clearReminder,
} from "../../redux/slices/medicationSlice";

import useAuthSession from "../../../authPatient/hooks/useAuthSession";
import AddPrescriptionModal from "../AddModal/AddModal";
import uploadIcon from "../../../assets/icons/Icon_upload.png";
import "./MedicationListCard.css";
import "../PrescriptionCard/PrescriptionCard.css"

/* ============ CONSTANTS ============ */
const TrackingMode = {
  DAILY: "daily",
  SPECIFIC_DAYS: "specificDays",
  LIFETIME: "lifetime",
};

const DAYS_SHORT_LABELS = {
  0: "Dim",
  1: "Lun",
  2: "Mar",
  3: "Mer",
  4: "Jeu",
  5: "Ven",
  6: "Sam",
};

/* =========================
 * Date helpers
 * ========================= */
const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const isSameDay = (a, b) =>
  startOfDay(a).getTime() === startOfDay(b).getTime();

const projectTimeToDate = (selectedDate, templateTime) => {
  const sd = new Date(selectedDate);
  const tt = new Date(templateTime);
  if (Number.isNaN(sd.getTime()) || Number.isNaN(tt.getTime())) return null;
  const out = new Date(sd);
  out.setHours(tt.getHours(), tt.getMinutes(), tt.getSeconds(), 0);
  return out;
};

const toHHMM = (dateLike) => {
  try {
    const d = new Date(dateLike);
    if (Number.isNaN(d.getTime())) return "--:--";
    return d.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Africa/Tunis",
    });
  } catch {
    return "--:--";
  }
};

/* =========================
 * Tracking helpers
 * ========================= */
const getTrackingMode = (med) =>
  med?.tracking?.mode || TrackingMode.DAILY;

const getSelectedDays = (med) => {
  const days = med?.tracking?.selectedDays;
  return Array.isArray(days) ? days : [];
};

const isTakingDay = (med, date) => {
  const mode = getTrackingMode(med);
  const d = new Date(date);
  switch (mode) {
    case TrackingMode.DAILY:
    case TrackingMode.LIFETIME:
      return true;
    case TrackingMode.SPECIFIC_DAYS: {
      const dayOfWeek = d.getDay();
      return getSelectedDays(med).includes(dayOfWeek);
    }
    default:
      return true;
  }
};

const isInDateRange = (med, date) => {
  const sel0 = startOfDay(date).getTime();
  const s = med.startDate ? startOfDay(med.startDate).getTime() : null;
  if (s != null && sel0 < s) return false;
  const mode = getTrackingMode(med);
  if (mode === TrackingMode.LIFETIME) return s == null || sel0 >= s;
  const e = med.endDate ? startOfDay(med.endDate).getTime() : null;
  if (s == null && e == null) return true;
  if (s == null) return sel0 <= e;
  if (e == null) return sel0 >= s;
  return sel0 >= s && sel0 <= e;
};

const getTrackingLabel = (med, t) => {
  const mode = getTrackingMode(med);
  switch (mode) {
    case TrackingMode.DAILY:
      return t("medicationListCard.tracking.daily", { defaultValue: "Tous les jours" });
    case TrackingMode.LIFETIME:
      return t("medicationListCard.tracking.lifetime", { defaultValue: "À vie" });
    case TrackingMode.SPECIFIC_DAYS: {
      const selectedDays = getSelectedDays(med);
      const order = [1, 2, 3, 4, 5, 6, 0];
      const dayLabels = [...selectedDays]
        .sort((a, b) => order.indexOf(a) - order.indexOf(b))
        .map((d) => DAYS_SHORT_LABELS[d] || "")
        .filter(Boolean)
        .join(", ");
      return (
        dayLabels ||
        t("medicationListCard.tracking.specificDays", { defaultValue: "Jours spécifiques" })
      );
    }
    default:
      return "";
  }
};

const getTrackingIcon = (med) => {
  const mode = getTrackingMode(med);
  switch (mode) {
    case TrackingMode.DAILY:       return <CalendarOutlined />;
    case TrackingMode.SPECIFIC_DAYS: return <SyncOutlined />;
    case TrackingMode.LIFETIME:    return <FieldTimeOutlined />;
    default:                       return <CalendarOutlined />;
  }
};

/* =========================
 * Image URL helpers
 * ========================= */
const gatewayBase = String(import.meta?.env?.VITE_GATEWAY_URL || "")
  .trim()
  .replace(/\/+$/, "");
const gatewayOrigin = gatewayBase
  ? gatewayBase.replace(/\/api\/?$/, "")
  : "";

const UPLOADS_BASE =
  import.meta?.env?.VITE_MEDICATION_UPLOADS_URL ||
  (gatewayOrigin
    ? `${gatewayOrigin}/api/medication/uploads`
    : "http://localhost:3010/uploads");

const IGNORED_IMAGE_VALUES = new Set([
  "default", "default.png", "default.jpg", "default.jpeg",
  "placeholder", "placeholder.png", "null", "undefined", "none", "",
]);

const isValidImageValue = (img) => {
  if (!img) return false;
  const value = String(img).trim().toLowerCase();
  return !!value && !IGNORED_IMAGE_VALUES.has(value);
};

/* =========================
 * COMPONENT
 * ========================= */
export default function MedicationListCard({
  title,
  date = new Date(),
  onEdit,
  onRemindToggle,
  /* PrescriptionCard props */
  prescriptionTitle,
  prescriptionDescription,
  prescriptionHighlight,
  prescriptionSeeNowText,
  onSeeNow,
  onAddPrescription,
  onAddMedication,
  onUpload,
  accept = ".pdf,.png,.jpg,.jpeg",
}) {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { effectiveUserId } = useAuthSession({ auto: true });
  const userId = effectiveUserId;

  // const meds = useSelector(selectActiveMedications);

  const {
  medications = [],
  activeMedications = [],
  loading,
} = useSelector(selectMedicationState);

const meds = activeMedications.length
  ? activeMedications
  : medications;

  const reminder = useSelector(selectMedicationReminder);

  /* ── Merged card title ── */
  const _title = title ?? t("medicationListCard.ui.title");

  /* ── PrescriptionCard i18n ── */
  const _pTitle       = prescriptionTitle       ?? t("prescriptionCard.ui.title");
  const _pDescription = prescriptionDescription ?? t("prescriptionCard.ui.description");
  const _dropText     = t("prescriptionCard.ui.dropText");
  const _btnAddPrescription = t("prescriptionCard.ui.addPrescription");
  const _btnAddMedication   = t("prescriptionCard.ui.addMedication");

  /* ── Prescription modal + dropzone ── */
  const inputRef    = useRef(null);
  const [dragOver, setDragOver]     = useState(false);
  const [openModal, setOpenModal]   = useState(false);

  const handleOpenPicker = () => inputRef.current?.click();

  const handleFiles = useCallback(
    (fileList) => {
      const files = Array.from(fileList || []);
      if (!files.length) return;
      onUpload?.(files);
      onAddPrescription?.(files);
    },
    [onUpload, onAddPrescription]
  );

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  /* =========================
   * Image URL resolver
   * ========================= */
  const resolveImageUrl = useCallback((img) => {
    if (!isValidImageValue(img)) return "";
    const value = String(img).trim();
    if (
      value.startsWith("http://") ||
      value.startsWith("https://") ||
      value.startsWith("data:")
    )
      return value;
    if (value.startsWith("/api/"))
      return gatewayOrigin ? `${gatewayOrigin}${value}` : value;
    const base = String(UPLOADS_BASE).replace(/\/+$/, "");
    let filename = value.replace(/^\/+/, "");
    if (filename.startsWith("uploads/")) filename = filename.slice("uploads/".length);
    return `${base}/${filename}`;
  }, []);

  /* =========================
   * Fetch medications
   * ========================= */
  useEffect(() => {
    if (userId) dispatch(fetchMedications({ userId }));
  }, [dispatch, userId]);

  /* =========================
   * Locale & formatters
   * ========================= */
  const locale = i18n?.language || "en";

  const fmtDowShort = useMemo(
    () => new Intl.DateTimeFormat(locale, { weekday: "short" }),
    [locale]
  );

  const fmtDateLeft = useCallback(
    (d) => {
      const parts = new Intl.DateTimeFormat(locale, {
        month: "long",
        weekday: "long",
        day: "numeric",
        year: "numeric",
      }).formatToParts(d);
      const month   = parts.find((p) => p.type === "month")?.value   || "";
      const weekday = parts.find((p) => p.type === "weekday")?.value || "";
      const day     = parts.find((p) => p.type === "day")?.value     || "";
      const year    = parts.find((p) => p.type === "year")?.value    || "";
      return t("medicationListCard.ui.dateLine", {
        month,
        weekday: String(weekday).toLowerCase(),
        day,
        year,
      });
    },
    [locale, t]
  );

  /* =========================
   * Days navigation
   * ========================= */
//   const [cursor, setCursor] = useState(0);

// const days = useMemo(() => {
//   const base = new Date(date);
//   base.setHours(0, 0, 0, 0);
//   const out = [];
//   for (let i = -3; i <= 3; i++) {
//     const d = new Date(base);
//     d.setDate(base.getDate() + i + cursor);
//     out.push(d);
//   }
//   return out;
// }, [date, cursor]);

// const centerIdx = Math.floor(days.length / 2);
// const selected = days[centerIdx];
// const viewingToday = isSameDay(selected, new Date());

const [selected, setSelected] = useState(startOfDay(date));

const days = useMemo(() => {
  const base = startOfDay(selected);
  const out = [];

  for (let i = -3; i <= 3; i++) {
    const d = new Date(base);
    d.setDate(base.getDate() + i);
    out.push(d);
  }

  return out;
}, [selected]);

const centerIdx = 3;
const viewingToday = isSameDay(selected, new Date());
  /* =========================
   * Days with meds indicator
   * ========================= */
  const daysWithMeds = useMemo(() => {
    if (!Array.isArray(meds)) return new Set();
    const result = new Set();
    days.forEach((day, idx) => {
      const hasMed = meds.some(
        (m) => isInDateRange(m, day) && isTakingDay(m, day)
      );
      if (hasMed) result.add(idx);
    });
    return result;
  }, [meds, days]);

  /* =========================
   * Filter meds for selected date
   * ========================= */
const medsForSelectedDate = useMemo(() => {
  if (!Array.isArray(meds)) return [];

  return meds.filter((m) => {
    if (m.isArchived) return false;
    return isInDateRange(m, selected) && isTakingDay(m, selected);
  });
}, [meds, selected]);

  /* =========================
   * Map meds with time pills
   * ========================= */
  const mappedMeds = useMemo(() => {
    if (!Array.isArray(medsForSelectedDate)) return [];

    const sorted = [...medsForSelectedDate].sort((a, b) => {
      const ta = new Date(a.createdAt || a.updatedAt || 0).getTime();
      const tb = new Date(b.createdAt || b.updatedAt || 0).getTime();
      return tb - ta;
    });

    return sorted.map((m) => {


    
      const doseLabel =
        m.amount > 1
          ? t("medicationListCard.ui.dosesPerDay")
          : t("medicationListCard.ui.dosePerDay");

      const times        = Array.isArray(m.times) ? m.times : [];
      const mode         = getTrackingMode(m);
      const hasValidImage = isValidImageValue(m.image);
      const imageUrl     = hasValidImage ? resolveImageUrl(m.image) : "";

      const timePills = [...times]
        .map((t0, idx) => {
          const projected = projectTimeToDate(selected, t0);
          return {
            compositeId: `${m.id || m._id}:${idx}:${startOfDay(selected).toISOString()}`,
            index: idx,
            label:   projected ? toHHMM(projected) : "--:--",
            rawTime: projected ? projected.toISOString() : t0,
            taken: viewingToday
              ? Array.isArray(m.takenPerDose)
                ? Boolean(m.takenPerDose[idx])
                : Boolean(m.isTaken)
              : false,
            disabled: !viewingToday,
          };
        })
        .filter(Boolean)
        .sort((a, b) => new Date(a.rawTime) - new Date(b.rawTime));

      return {
        medId: m.id || m._id,
        name: m.name,
        subtitle: `${m.amount} ${doseLabel}`,
        notes: "",
        quantity: m.quantity || "",
        medicationQuantity: m.medicationQuantity || 0,
        estimatedDaysCovered:
        m.medicationQuantity && m.amount
          ? Math.floor(m.medicationQuantity / m.amount)
          : 0,
        imageUrl,
        hasValidImage,
        timePills,
        trackingMode:  mode,
        trackingLabel: getTrackingLabel(m, t),
        trackingIcon:  getTrackingIcon(m),
        isLifetime:     mode === TrackingMode.LIFETIME,
        isSpecificDays: mode === TrackingMode.SPECIFIC_DAYS,
        selectedDays: [...getSelectedDays(m)],
        startDate: m.startDate,
        endDate:   m.endDate,

       
      };
    });
  }, [medsForSelectedDate, selected, viewingToday, t, resolveImageUrl]);

  /* =========================
   * Local state for optimistic updates
   * ========================= */


  const localMeds = mappedMeds;

  /* =========================
   * Failed images tracker
   * ========================= */
  const [failedImages, setFailedImages] = useState(new Set());
  const handleImageError = useCallback((medId) => {
    setFailedImages((prev) => new Set([...prev, medId]));
  }, []);

  /* =========================
   * Reminder status
   * ========================= */
  const reminderStatus = useMemo(() => {
    if (!reminder?.dismissed) return { show: false, time: "--:--", name: "" };
    const medId     = String(reminder.medicationId);
    const doseIndex = Number(reminder.doseIndex ?? 0);
    const med  = localMeds.find((m) => String(m.medId) === medId);
    const pill = med?.timePills?.find((p) => p.index === doseIndex);
    return {
      show: !pill?.taken,
      time: pill?.label || toHHMM(reminder.reminderTime),
      name: med?.name || reminder?.name || t("medicationListCard.ui.thisMedication"),
    };
  }, [reminder, localMeds, t]);

  /* =========================
   * Toggle single pill (dose)
   * ========================= */
  const onTogglePill = useCallback(
    async (medId, pillIndex) => {
      const currentMed  = localMeds.find((m) => m.medId === medId);
      const currentPill = currentMed?.timePills?.find((p) => p.index === pillIndex);
      if (!currentPill) return;

      if (currentPill.disabled) {
        notification.info({ message: t("medicationListCard.notify.onlyToday") });
        return;
      }

      const nextTaken = !currentPill.taken;

      // setLocalMeds((prev) =>
      //   prev.map((med) =>
      //     med.medId === medId
      //       ? {
      //           ...med,
      //           timePills: med.timePills.map((p) =>
      //             p.index === pillIndex ? { ...p, taken: nextTaken } : p
      //           ),
      //         }
      //       : med
      //   )
      // );

      if (!userId) return;

      if (nextTaken) {
        try {
          const res = await dispatch(
            takeDose({
              userId,
              id: medId,
              index: pillIndex,
              forDate: viewingToday
                ? undefined
                : startOfDay(selected).toISOString().slice(0, 10),
            })
          );

          if (res.meta?.requestStatus === "rejected") {
            throw new Error(
              res.payload ||
                res.error?.message ||
                t("medicationListCard.notify.failMarkTaken")
            );
          }

          notification.success({ message: t("medicationListCard.notify.markedTaken") });

          if (
            reminder &&
            String(reminder.medicationId) === String(medId) &&
            Number(reminder.doseIndex ?? 0) === Number(pillIndex)
          ) {
            dispatch(clearReminder());
          }
        } catch (e) {
          // setLocalMeds((prev) =>
          //   prev.map((med) =>
          //     med.medId === medId
          //       ? {
          //           ...med,
          //           timePills: med.timePills.map((p) =>
          //             p.index === pillIndex ? { ...p, taken: !nextTaken } : p
          //           ),
          //         }
          //       : med
          //   )
          // );
          dispatch(fetchMedications({ userId }));
          notification.error({
            message:     t("medicationListCard.notify.failedUpdate"),
            description: String(e.message || e),
          });
        }
      } else {
        notification.info({ message: t("medicationListCard.notify.cannotUnmark") });
        // setLocalMeds((prev) =>
        //   prev.map((med) =>
        //     med.medId === medId
        //       ? {
        //           ...med,
        //           timePills: med.timePills.map((p) =>
        //             p.index === pillIndex ? { ...p, taken: true } : p
        //           ),
        //         }
        //       : med
        //   )
        // );
   
      }
    },
    [dispatch, userId, localMeds, reminder, t, viewingToday, selected]
  );

  /* =========================
   * Toggle global (all doses)
   * ========================= */
  const onToggleGlobal = useCallback(
    async (med) => {
      if (!viewingToday) {
        notification.info({ message: t("medicationListCard.notify.onlyToday") });
        return;
      }
      const allTaken =
        med.timePills.length > 0 && med.timePills.every((p) => p.taken);
      if (allTaken) {
        notification.info({ message: t("medicationListCard.notify.allTaken") });
        return;
      }
      const nextUntaken = med.timePills.find((p) => !p.taken);
      if (!nextUntaken) return;
      await onTogglePill(med.medId, nextUntaken.index);
    },
    [onTogglePill, viewingToday, t]
  );

  /* =========================
   * Format date range
   * ========================= */
  const formatDateRange = useCallback(
    (med) => {
      const fmt = (d) =>
        new Date(d).toLocaleDateString(locale, { day: "2-digit", month: "short" });

      if (med.isLifetime) {
        const start = med.startDate ? fmt(med.startDate) : "";
        return start
          ? `${start} → ∞`
          : t("medicationListCard.tracking.lifetimeShort", { defaultValue: "∞" });
      }

      const start = med.startDate ? fmt(med.startDate) : "";
      const end   = med.endDate   ? fmt(med.endDate)   : "";
      if (start && end) return `${start} → ${end}`;
      if (start)        return `${start} →`;
      return "";
    },
    [locale, t]
  );

  /* =========================
   * Render
   * ========================= */
  return (
    <> 
      <div className="mlc-card">
        {/* Header */}
        <div className="mlc-head">
          <h3 className="mlc-title">{_title}</h3>
          <Tooltip title={t("medicationListCard.ui.editSchedule")}>
            <button className="mlc-add-btn" onClick={onEdit} type="button">
               <PlusOutlined />
    <span> {t("prescriptionCard.ui.addMedication")}</span>
            </button>
          </Tooltip>
        </div>

        {/* Reminder alert */}
        {reminderStatus.show && (
          <div className="mlc-alert" role="alert">
            <span className="mlc-alert-badge">
              <ExclamationCircleOutlined />
            </span>
            <span className="mlc-alert-text">
              {t("medicationListCard.ui.missedPrefix")}{" "}
              <strong>{reminderStatus.name}</strong>{" "}
              {t("medicationListCard.ui.missedAt")} {reminderStatus.time} —{" "}
              {t("medicationListCard.ui.missedSuffix")}
            </span>
          </div>
        )}

        {/* Date display */}
        <div className="mlc-date-row">
          <div className="mlc-date-left">{fmtDateLeft(selected)}</div>
        </div>

        {/* Day pills navigation */}
        <div className="mlc-days">
          <button
            className="mlc-nav"
            // onClick={() => setCursor((c) => c - 1)}
            onClick={() =>
  setSelected((prev) => {
    const d = new Date(prev);
    d.setDate(d.getDate() - 1);
    return startOfDay(d);
  })
}
            aria-label={t("medicationListCard.ui.prevDays")}
            type="button"
          >
            <LeftOutlined />
          </button>

          <div
            className="mlc-day-pills"
            role="tablist"
            aria-label={t("medicationListCard.ui.days")}
          >
            {days.map((d, idx) => {
              const isSel  = idx === centerIdx;
              const hasMeds = daysWithMeds.has(idx);
              return (
                <button
                  key={d.toDateString() + idx}
                  className={`mlc-day-pill ${isSel ? "is-active" : ""} ${hasMeds ? "has-meds" : ""}`}
                  // onClick={() => setCursor((c) => c + (idx - centerIdx))}
          onClick={() => setSelected(startOfDay(d))}
                  type="button"
                >
                  <div className="mlc-day-num">
                    {String(d.getDate()).padStart(2, "0")}
                  </div>
                  <div className={`mlc-day-dow ${isSel ? "is-active" : ""}`}>
                    {fmtDowShort.format(d)}
                  </div>
                  {hasMeds && <span className="mlc-day-dot" aria-hidden />}
                </button>
              );
            })}
          </div>

          <button
            className="mlc-nav"
            // onClick={() => setCursor((c) => c + 1)}
      onClick={() =>
  setSelected((prev) => {
    const d = new Date(prev);
    d.setDate(d.getDate() + 1);
    return startOfDay(d);
  })
}
            aria-label={t("medicationListCard.ui.nextDays")}
            type="button"
          >
            <RightOutlined />
          </button>
        </div>

        {/* Medications list */}


        <div className="mlc-list">
          {loading && !localMeds.length ? (
            <div className="mlc-empty">{t("medicationListCard.ui.loading")}</div>
          ) : localMeds.length === 0 ? (
            <div className="mlc-empty">
              <div className="mlc-empty-text">
                {t("medicationListCard.ui.emptyForDate")}
              </div>
              {!viewingToday && (
                <div className="mlc-empty-hint">
                  {t("medicationListCard.ui.noMedsForThisDay", {
                    defaultValue: "Aucun médicament prévu ce jour",
                  })}
                </div>
              )}
            </div>
          ) : (
            localMeds.map((med) => {
              const total      = med.timePills.length;
              const takenCount = med.timePills.filter((p) => p.taken).length;
              const allTaken   =
                viewingToday && total > 0 && takenCount === total;
              const toggleLabel = allTaken
                ? t("medicationListCard.ui.taken")
                : t("medicationListCard.ui.notTaken");

              const showImage =
                med.hasValidImage &&
                med.imageUrl &&
                !failedImages.has(med.medId);

              return (
                <div
                  key={med.medId}
                  className={`mlc-item ${allTaken ? "mlc-item--done" : ""}`}
                >
                  {/* Time pills */}
                  <div className="mlc-time">
                    <div className="mlc-time-pills">
                      {med.timePills.map((tp) => (
                        <button
                          key={tp.compositeId}
                          className={`mlc-time-pill ${tp.taken ? "is-taken" : ""} ${
                            tp.disabled ? "is-disabled" : ""
                          }`}
                          onClick={() => onTogglePill(med.medId, tp.index)}
                          type="button"
                          aria-pressed={tp.taken}
                          disabled={tp.disabled}
                          title={
                            tp.disabled
                              ? t("medicationListCard.ui.onlyTodayActionable")
                              : tp.taken
                              ? t("medicationListCard.ui.taken")
                              : t("medicationListCard.ui.markTaken")
                          }
                        >
                          {tp.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Separator line */}
                  <div className="mlc-line" aria-hidden />

                  {/* Main content */}
                  <div className="mlc-main">
                    <div className="mlc-header-line">
                      <div className="mlc-name-with-image">
                        {showImage && (
                          <img
                            src={med.imageUrl}
                            alt={med.name}
                            className="mlc-med-image"
                            onError={() => handleImageError(med.medId)}
                          />
                        )}
                        <div className="mlc-name">{med.name}</div>
                      </div>

                      <div className="mlc-notes">
                        {t("medicationListCard.ui.notes")}: {med.notes || "—"}
                      </div>
                    </div>

                    <div className="mlc-sub">
                      <span>{med.subtitle}</span>

                      {med.quantity && (
                        <span className="mlc-extra-inline">
                          • Dosage: {med.quantity}
                        </span>
                      )}

                      {med.medicationQuantity > 0 && (
                        <span className="mlc-extra-inline">
                          • Stock: {med.medicationQuantity}
                        </span>
                      )}

                      {med.estimatedDaysCovered > 0 && (
                        <span className="mlc-extra-inline">
                          • Covers {med.estimatedDaysCovered} day(s)
                        </span>
                      )}
                    </div>

                    {/* Tracking info */}
                    <div className="mlc-tracking-info">
                      <span
                        className="mlc-tracking-badge"
                        data-mode={med.trackingMode}
                      >
                        {med.trackingIcon}
                        <span className="mlc-tracking-label">
                          {med.trackingLabel}
                        </span>
                      </span>
                      <span className="mlc-date-range">
                        {formatDateRange(med)}
                      </span>
                    </div>

                    {/* Specific days mini indicators */}
                    {med.isSpecificDays && (
                      <div className="mlc-specific-days">
                        {[1, 2, 3, 4, 5, 6, 0].map((dayNum) => {
                          const isActive = med.selectedDays.includes(dayNum);
                          const isToday =
                            new Date().getDay() === dayNum && viewingToday;
                          return (
                            <span
                              key={dayNum}
                              className={`mlc-day-mini ${isActive ? "active" : ""} ${
                                isToday ? "today" : ""
                              }`}
                            >
                              {DAYS_SHORT_LABELS[dayNum]?.[0]}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Toggle button */}
                  <button
                    className={`mlc-toggle ${allTaken ? "is-on" : ""}`}
                    onClick={() => onToggleGlobal(med)}
                    role="switch"
                    aria-checked={allTaken}
                    type="button"
                    disabled={!viewingToday}
                    title={
                      !viewingToday
                        ? t("medicationListCard.ui.onlyTodayActionable")
                        : ""
                    }
                  >
                    <span className={`mlc-status ${allTaken ? "is-taken" : ""}`}>
                      {toggleLabel}
                    </span>
                    <span className="mlc-dot" aria-hidden />
                  </button>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* ===================================================
          MODAL — Ajouter ordonnance / traitement
          =================================================== */}
      <AddPrescriptionModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        onUpload={onUpload}
        accept={accept}
        onCreatePrescription={() => {
          setOpenModal(false);
          onAddPrescription?.();
        }}
        onCreateMedication={() => {
          setOpenModal(false);
          onAddMedication?.();
        }}
      />
    </>
  );
}
